import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { Spinner } from '@/components/Spinner';
import { Page } from '@/components/layout/Page';
import { cls } from '@/utils/cls';

// ── Color tokens (mock CSS vars → hex) ──────────────────────────────────────
const C = {
  blueDark: '#274483',
  positive: '#2A8055',
  warning: '#D27A24',
  text1: '#1A1C2E',
  text2: '#5C5F72',
  text3: '#9295A5',
  border: '#E4E0D8',
  bgWarm: '#F5F6F8',
  white: '#FFFFFF',
};

// ── Types ───────────────────────────────────────────────────────────────────
// Live data from the IO service (server-side `/api/data-app/transformations/*`).
type TableEntry = { id: string; name: string; desc: string; lastUpdated: string | null };
type Layer = {
  id: string;
  name: string;
  color: string;
  short: string;
  business: string;
  tables: number;
  records: string; // still invented (no IO source)
  freshness: string; // static fallback
  coverage: string; // still invented
  lastUpdated: string | null; // newest table in the layer (datasets.last_updated)
  tables_list: TableEntry[];
};
type FieldDef = { name: string; type: string };
type SampleRow = Record<string, unknown>;
// Lazy per-table detail (one `/read` preview call on click).
type LinkItem = { id: string | null; label: string };
type Narrative = { source: LinkItem[]; keystoneUse: string; feedsInto: LinkItem[]; keystoneDerives: string[] };
type TableDetail = {
  id: string;
  name: string;
  layer: string;
  desc: string;
  snapshot: string | null;
  fields: FieldDef[];
  sample: SampleRow[];
  lastUpdated: string | null;
  narrative?: Narrative;
  inLineage?: boolean;
};

const DAY_MS = 24 * 60 * 60 * 1000;
type PlNode = { id: string; layer: string; label: string; sub: string; color: string; col: number };
type PlEdge = { s: string; t: string };
type PlLineage = { up: string[]; dn: string[] };
type PlTableDetail = {
  name: string;
  layer: string;
  desc: string;
  keystone: string;
  records: string;
  downstream: string[];
};
type Pipeline = {
  nodes: PlNode[];
  edges: PlEdge[];
  lineage: Record<string, PlLineage>;
  tableDetails: Record<string, PlTableDetail>;
};
type LayersData = { layers: Layer[]; pipeline: Pipeline };

// Format an ISO/space timestamp (e.g. "2026-06-18 00:20:11+00:00") as relative time.
function formatRelative(ts: string | null | undefined): string | null {
  if (!ts) return null;
  const ms = Date.parse(ts.replace(' ', 'T'));
  if (Number.isNaN(ms)) return null;
  const diff = Date.now() - ms;
  if (diff < 60000) return 'just now';
  const min = Math.floor(diff / 60000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}
const ageDays = (ts: string | null | undefined): number | null => {
  if (!ts) return null;
  const ms = Date.parse(ts.replace(' ', 'T'));
  return Number.isNaN(ms) ? null : (Date.now() - ms) / 86400000;
};

const LABEL_STYLE = 'text-[9px] font-bold uppercase tracking-[0.08em]';
// 8.5px section labels in the mock use letter-spacing .07em
const LABEL_SM = 'text-[8.5px] font-bold uppercase tracking-[0.07em]';

export function DataTransformations() {
  const { data, isLoading } = useQuery<LayersData>({
    queryKey: ['data-app', 'transformations', 'layers'],
    queryFn: () => fetch('/api/data-app/transformations/layers').then((r) => r.json()),
    staleTime: DAY_MS,
    gcTime: DAY_MS,
  });

  // Permalink: ?id=<layer/table> (a table — contains a slash) or ?id=<layer> (a layer). Fully resolvable —
  // the table detail is fetched by id on demand, so a shared link reconstructs the same view.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlId = searchParams.get('id');
  const setUrl = (id: string | null) => {
    setSearchParams(
      (prev) => {
        if (id) prev.set('id', id);
        else prev.delete('id');
        return prev;
      },
      { replace: true },
    );
  };

  const [view, setView] = useState<'table' | 'pipeline'>('table');
  // Default to the first pipeline layer (raw) on initial load — honoring any ?id permalink.
  const [selectedLayerId, setSelectedLayerId] = useState<string>(
    urlId ? (urlId.includes('/') ? urlId.slice(0, urlId.indexOf('/')) : urlId) : 'raw',
  );
  const [selectedTableId, setSelectedTableId] = useState<string | null>(urlId && urlId.includes('/') ? urlId : null);
  // Node to highlight when entering the pipeline view (e.g. via "See lineage").
  const [pipelineFocusId, setPipelineFocusId] = useState<string | null>(null);

  const layers = data?.layers ?? [];
  const selectedLayer = layers.find((l) => l.id === selectedLayerId);

  // Lazy per-table detail (real fields + ≤2 sample rows) — one /read call per click.
  const { data: tableDetail, isFetching: tableLoading } = useQuery<TableDetail>({
    queryKey: ['data-app', 'transformations', 'table', selectedTableId],
    queryFn: () => fetch(`/api/data-app/transformations/table/${selectedTableId}`).then((r) => r.json()),
    enabled: !!selectedTableId,
    staleTime: DAY_MS,
    gcTime: DAY_MS,
  });

  const selectLayer = (id: string) => {
    setSelectedLayerId(id);
    setSelectedTableId(null);
    setUrl(id);
  };

  // Select a table within the current layer (from the grid).
  const selectTable = (tableId: string) => {
    setSelectedTableId(tableId);
    setUrl(tableId);
  };

  // Open a specific table's detail by id ("<layer>/<table>") — used by lineage links and the pipeline view.
  const openTable = (tableId: string) => {
    const slash = tableId.indexOf('/');
    if (slash < 0) return;
    setView('table');
    setSelectedLayerId(tableId.slice(0, slash));
    setSelectedTableId(tableId);
    setUrl(tableId);
  };

  // Jump from a table's detail to the Pipeline view with that table highlighted.
  const openLineage = (tableId: string) => {
    setPipelineFocusId(tableId);
    setView('pipeline');
  };

  const viewToggle = (
    <div
      className="flex items-center self-start flex-shrink-0 overflow-hidden mt-1"
      style={{ background: C.bgWarm, border: `1px solid ${C.border}`, borderRadius: 6 }}
    >
      <button
        type="button"
        onClick={() => setView('table')}
        className="text-[11px] px-3 py-[5px] cursor-pointer"
        style={
          view === 'table'
            ? { fontWeight: 700, background: C.blueDark, color: 'white', border: 'none' }
            : { fontWeight: 600, background: 'transparent', color: C.text2, border: 'none' }
        }
      >
        ⊞ Table
      </button>
      <button
        type="button"
        onClick={() => {
          setPipelineFocusId(null);
          setView('pipeline');
        }}
        className="text-[11px] px-3 py-[5px] cursor-pointer"
        style={
          view === 'pipeline'
            ? { fontWeight: 700, background: C.blueDark, color: 'white', border: 'none' }
            : { fontWeight: 600, background: 'transparent', color: C.text2, border: 'none' }
        }
      >
        ⟳ Pipeline
      </button>
    </div>
  );

  return (
    <Page
      title="Transformations"
      subheader="How your raw operational data becomes behavioral intelligence — five transformation layers from source system extract to AI-ready encodings."
      actions={viewToggle}
      fillHeight
    >
      <div className="flex flex-col h-full min-h-0">
        {isLoading ? (
          <div
            className="flex flex-1 min-h-0 overflow-hidden bg-white"
            style={{ border: `1px solid ${C.border}`, borderRadius: 8 }}
          >
            <CenteredMessage spinning title="Loading transformations…" sub="Fetching pipeline layers and tables." />
          </div>
        ) : view === 'table' ? (
          <TableView
            layers={layers}
            selectedLayer={selectedLayer}
            selectedLayerId={selectedLayerId}
            selectedTableId={selectedTableId}
            tableDetail={tableDetail ?? null}
            tableLoading={tableLoading}
            onSelectLayer={selectLayer}
            onSelectTable={selectTable}
            onNavigate={openTable}
            onSeeLineage={openLineage}
          />
        ) : (
          data && (
            <PipelineView
              pipeline={data.pipeline}
              focusId={pipelineFocusId}
              onViewTableDetail={openTable}
              onBack={() => setView('table')}
            />
          )
        )}
      </div>
    </Page>
  );
}

// ── TABLE VIEW ───────────────────────────────────────────────────────────────
function TableView({
  layers,
  selectedLayer,
  selectedLayerId,
  selectedTableId,
  tableDetail,
  tableLoading,
  onSelectLayer,
  onSelectTable,
  onNavigate,
  onSeeLineage,
}: {
  layers: Layer[];
  selectedLayer?: Layer;
  selectedLayerId: string;
  selectedTableId: string | null;
  tableDetail: TableDetail | null;
  tableLoading: boolean;
  onSelectLayer: (id: string) => void;
  onSelectTable: (id: string) => void;
  onNavigate: (tableId: string) => void;
  onSeeLineage: (tableId: string) => void;
}) {
  return (
    <div
      className="flex flex-1 min-h-0 overflow-hidden bg-white"
      style={{ border: `1px solid ${C.border}`, borderRadius: 8 }}
    >
      {/* LEFT: Layer selector */}
      <div
        className="w-[280px] flex-shrink-0 flex flex-col overflow-y-auto"
        style={{ borderRight: `1px solid ${C.border}` }}
      >
        <div className="px-[14px] pt-[14px] pb-2">
          <div className={cls(LABEL_STYLE, 'mb-[10px]')} style={{ color: C.text3 }}>
            Pipeline Layers
          </div>
          <div>
            {layers.map((l, i) => (
              <div key={l.id}>
                {i > 0 && (
                  <div style={{ width: 2, height: 12, background: C.border, margin: '0 auto 4px', marginLeft: 22 }} />
                )}
                <LayerRow layer={l} active={selectedLayerId === l.id} onClick={() => onSelectLayer(l.id)} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* RIGHT: Detail panel */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        {selectedTableId ? (
          tableLoading && !tableDetail ? (
            <CenteredMessage spinning title="Loading table…" sub="Fetching fields and sample rows" />
          ) : tableDetail ? (
            <TableDetailPanel detail={tableDetail} onNavigate={onNavigate} onSeeLineage={onSeeLineage} />
          ) : (
            <CenteredMessage
              icon="⚠"
              title="Couldn't load table"
              sub="The IO service did not return data for this table."
            />
          )
        ) : selectedLayer ? (
          <LayerDetailPanel layer={selectedLayer} layers={layers} onSelectTable={onSelectTable} />
        ) : (
          <EmptyState />
        )}
      </div>
    </div>
  );
}

function LayerRow({ layer, active, onClick }: { layer: Layer; active: boolean; onClick: () => void }) {
  const rel = formatRelative(layer.lastUpdated) ?? layer.freshness;
  const d = ageDays(layer.lastUpdated);
  const warn = d != null ? d > 7 : layer.freshness.includes('7');
  return (
    <div
      onClick={onClick}
      className="flex items-center gap-[10px] px-[10px] py-2 cursor-pointer mb-1 transition-colors hover:bg-[rgba(39,68,131,.05)]"
      style={{
        borderRadius: 6,
        ...(active ? { background: 'rgba(39,68,131,.08)', borderLeft: `3px solid ${C.blueDark}` } : {}),
      }}
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0"
        style={{ background: layer.color }}
      >
        {layer.name[0]}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-bold" style={{ color: C.text1 }}>
          {layer.name}
        </div>
        <div className="text-[9.5px] mt-px" style={{ color: C.text3 }}>
          {layer.tables} tables · {layer.records}
        </div>
      </div>
      <div
        className="text-[9px] font-bold px-[5px] py-[2px] rounded-full whitespace-nowrap"
        style={{
          background: warn ? 'rgba(210,122,36,.1)' : 'rgba(42,128,85,.1)',
          color: warn ? C.warning : C.positive,
        }}
      >
        {rel}
      </div>
    </div>
  );
}

function CenteredMessage({
  icon,
  title,
  sub,
  spinning,
}: {
  icon?: string;
  title: string;
  sub: string;
  spinning?: boolean;
}) {
  return (
    <div
      className="flex-1 flex flex-col items-center justify-center gap-[10px] p-10 text-center"
      style={{ color: C.text3 }}
    >
      {spinning ? (
        <Spinner size={28} />
      ) : (
        <div className="text-[36px]" style={{ opacity: 0.15 }}>
          {icon}
        </div>
      )}
      <div className="text-[13px] font-semibold" style={{ color: C.text2 }}>
        {title}
      </div>
      <div className="text-[11.5px] max-w-[340px] leading-[1.7]">{sub}</div>
    </div>
  );
}

function EmptyState() {
  return (
    <CenteredMessage
      icon="⟳"
      title="Select a layer or table"
      sub="Click any layer to explore what's inside, or switch to Pipeline view to see how data flows between tables."
    />
  );
}

function LayerDetailPanel({
  layer,
  layers,
  onSelectTable,
}: {
  layer: Layer;
  layers: Layer[];
  onSelectTable: (id: string) => void;
}) {
  const metrics = [
    { lbl: 'Tables', val: layer.tables },
    { lbl: 'Records', val: layer.records },
    { lbl: 'Last Updated', val: formatRelative(layer.lastUpdated) ?? layer.freshness },
    { lbl: 'Coverage', val: layer.coverage },
  ];
  return (
    <div className="flex-1 overflow-y-auto flex flex-col">
      {/* Layer header */}
      <div className="px-6 py-4 flex-shrink-0 bg-white" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className="flex items-center gap-3 mb-2">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-[14px] font-bold text-white"
            style={{ background: layer.color }}
          >
            {layer.name[0]}
          </div>
          <div>
            <div className="text-[16px] font-bold" style={{ color: C.text1 }}>
              {layer.name} Layer
            </div>
            <div className="text-[10.5px]" style={{ color: C.text3 }}>
              {layer.short}
            </div>
          </div>
        </div>
        <div className="text-[11.5px] leading-[1.6] mb-3" style={{ color: C.text2 }}>
          {layer.business}
        </div>
        <div className="grid grid-cols-4 overflow-hidden" style={{ border: `1px solid ${C.border}`, borderRadius: 6 }}>
          {metrics.map((m, i) => (
            <div key={m.lbl} className="px-3 py-2" style={i < 3 ? { borderRight: `1px solid ${C.border}` } : {}}>
              <div className={LABEL_SM} style={{ color: C.text3 }}>
                {m.lbl}
              </div>
              <div className="text-[14px] font-bold mt-[2px]" style={{ color: C.text1 }}>
                {m.val}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Pipeline position */}
      <div
        className="px-6 py-[14px] flex-shrink-0"
        style={{ borderBottom: `1px solid ${C.border}`, background: C.bgWarm }}
      >
        <div className={cls(LABEL_STYLE, 'mb-[10px]')} style={{ color: C.text3 }}>
          Pipeline Position
        </div>
        <div className="flex items-center gap-1">
          {layers.map((l, i) => (
            <div key={l.id} className="flex items-center gap-1">
              <div
                className="px-[10px] py-1 rounded-full text-[10px] font-bold"
                style={{
                  background: l.id === layer.id ? l.color : C.border,
                  color: l.id === layer.id ? 'white' : C.text3,
                }}
              >
                {l.name}
              </div>
              {i < layers.length - 1 && (
                <div className="text-[12px]" style={{ color: C.border }}>
                  →
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Tables in this layer */}
      <div className="px-6 py-[14px] flex-1">
        <div className={cls(LABEL_STYLE, 'mb-3')} style={{ color: C.text3 }}>
          {layer.tables} tables in this layer — click any table for field detail
        </div>
        {layer.tables_list.length === 0 ? (
          <div className="text-[11px]" style={{ color: C.text3 }}>
            No tables found for this layer.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-[10px]">
            {layer.tables_list.map((t) => {
              const rel = formatRelative(t.lastUpdated);
              return (
                <div
                  key={t.id}
                  onClick={() => onSelectTable(t.id)}
                  className="px-[14px] py-3 cursor-pointer bg-white"
                  style={{ border: `1px solid ${C.border}`, borderRadius: 8, transition: 'all .12s', minWidth: 0 }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = C.blueDark;
                    e.currentTarget.style.background = 'rgba(39,68,131,.03)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = C.border;
                    e.currentTarget.style.background = C.white;
                  }}
                >
                  <div
                    className="text-[11px] font-bold font-mono mb-1"
                    style={{ color: C.blueDark, overflowWrap: 'anywhere' }}
                  >
                    {t.name}
                  </div>
                  {t.desc && (
                    <div className="text-[10.5px] leading-[1.4] mb-[6px]" style={{ color: C.text2 }}>
                      {t.desc}
                    </div>
                  )}
                  {rel && (
                    <div className="text-[9px]" style={{ color: C.text3 }}>
                      Updated {rel}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Render a sample-cell value. Objects/arrays show a compact indicator with the full JSON
// on hover (native title tooltip) instead of "[object Object]".
function renderCellValue(v: unknown): React.ReactNode {
  if (v === null || v === undefined) return '';
  if (typeof v === 'object') {
    const json = JSON.stringify(v, null, 2);
    const label = Array.isArray(v) ? `[ ${v.length} ]` : '{ … }';
    return (
      <span title={json} style={{ borderBottom: `1px dashed ${C.text3}`, cursor: 'help', color: C.blueDark }}>
        {label}
      </span>
    );
  }
  return String(v);
}

// A Source/Feeds-Into entry: a link to another table's detail when its id is known, else plain text.
function NarrativeLink({ item, onNavigate }: { item: LinkItem; onNavigate: (id: string) => void }) {
  if (!item.id) return <span style={{ color: C.text2, overflowWrap: 'anywhere' }}>{item.label}</span>;
  const targetId = item.id;
  return (
    <button
      type="button"
      onClick={() => onNavigate(targetId)}
      className="text-left p-0 cursor-pointer"
      style={{
        color: C.blueDark,
        background: 'none',
        border: 'none',
        textDecoration: 'underline',
        overflowWrap: 'anywhere',
      }}
    >
      {item.label}
    </button>
  );
}

function TableDetailPanel({
  detail,
  onNavigate,
  onSeeLineage,
}: {
  detail: TableDetail;
  onNavigate: (id: string) => void;
  onSeeLineage: (id: string) => void;
}) {
  const rel = formatRelative(detail.lastUpdated);
  const hasRows = detail.sample.length > 0;
  const columns = hasRows ? Object.keys(detail.sample[0]) : detail.fields.map((f) => f.name);
  const inLineage = !!detail.inLineage;
  return (
    <div className="flex-1 overflow-y-auto flex flex-col">
      {/* Table header */}
      <div className="px-6 pt-4 pb-[14px] flex-shrink-0 bg-white" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className={LABEL_STYLE} style={{ color: C.text3 }}>
              {detail.layer} Layer{rel ? ` · Last updated ${rel}` : ''}
            </div>
            <div
              className="text-[16px] font-bold font-mono mb-1 mt-1"
              style={{ color: C.text1, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}
            >
              {detail.name}
            </div>
            {detail.desc && (
              <div className="text-[11.5px] leading-[1.6]" style={{ color: C.text2 }}>
                {detail.desc}
              </div>
            )}
            <div className="text-[10px] mt-2" style={{ color: C.text3 }}>
              {detail.fields.length} fields
            </div>
          </div>
          <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3 }}>
            <button
              type="button"
              disabled={!inLineage}
              onClick={() => inLineage && onSeeLineage(detail.id)}
              title={
                inLineage
                  ? 'Show this table in the lineage pipeline'
                  : "Infrastructure table — it isn't part of the data lineage graph, so there's nothing to show in the pipeline."
              }
              className="text-[11px] font-semibold whitespace-nowrap"
              style={{
                padding: '5px 10px',
                borderRadius: 6,
                border: `1px solid ${inLineage ? C.blueDark : C.border}`,
                background: inLineage ? C.blueDark : C.bgWarm,
                color: inLineage ? 'white' : C.text3,
                cursor: inLineage ? 'pointer' : 'not-allowed',
              }}
            >
              See lineage →
            </button>
            {!inLineage && (
              <div className="text-[9px] text-right" style={{ color: C.text3, maxWidth: 130 }}>
                Not part of the lineage graph
              </div>
            )}
          </div>
        </div>
        {detail.narrative?.keystoneUse && (
          <div
            className="px-3 py-2 text-[11px] leading-[1.5] mt-3"
            style={{
              background: 'rgba(39,68,131,.05)',
              borderRadius: 6,
              borderLeft: `3px solid ${C.blueDark}`,
              color: C.text2,
            }}
          >
            <strong style={{ color: C.blueDark }}>How Keystone uses this:</strong> {detail.narrative.keystoneUse}
          </div>
        )}
      </div>

      {/* Source + Feeds Into (linked to their table detail when known) */}
      {detail.narrative && (detail.narrative.source.length > 0 || detail.narrative.feedsInto.length > 0) && (
        <div className="grid grid-cols-2 flex-shrink-0" style={{ borderBottom: `1px solid ${C.border}` }}>
          <div className="px-6 py-3" style={{ borderRight: `1px solid ${C.border}` }}>
            <div className={LABEL_SM} style={{ color: C.text3 }}>
              Source
            </div>
            <div className="mt-[6px] flex flex-col gap-[2px] text-[11px] font-mono">
              {detail.narrative.source.length === 0 ? (
                <span style={{ color: C.text3 }}>—</span>
              ) : (
                detail.narrative.source.map((it, i) => <NarrativeLink key={i} item={it} onNavigate={onNavigate} />)
              )}
            </div>
          </div>
          <div className="px-6 py-3">
            <div className={cls(LABEL_SM, 'mb-[6px]')} style={{ color: C.text3 }}>
              Feeds Into
            </div>
            <div className="flex flex-col gap-[2px] text-[10.5px] font-mono">
              {detail.narrative.feedsInto.length === 0 ? (
                <span style={{ color: C.text3 }}>—</span>
              ) : (
                detail.narrative.feedsInto.map((it, i) => (
                  <div key={i}>
                    <span style={{ color: C.text3 }}>→ </span>
                    <NarrativeLink item={it} onNavigate={onNavigate} />
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* What Keystone Derives */}
      {detail.narrative && detail.narrative.keystoneDerives.length > 0 && (
        <div
          className="px-6 py-3 flex-shrink-0"
          style={{ borderBottom: `1px solid ${C.border}`, background: C.bgWarm }}
        >
          <div className={cls(LABEL_SM, 'mb-2')} style={{ color: C.text3 }}>
            What Keystone Derives From This Table
          </div>
          <div className="flex flex-wrap gap-[6px]">
            {detail.narrative.keystoneDerives.map((d) => (
              <span
                key={d}
                className="text-[10px] font-semibold px-[9px] py-[3px] rounded-full"
                style={{ background: C.white, border: `1px solid ${C.border}`, color: C.text2 }}
              >
                {d}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Sample data */}
      <div className="px-6 py-3 flex-shrink-0">
        <div className={cls(LABEL_SM, 'mb-[10px]')} style={{ color: C.text3 }}>
          Sample Data — {detail.sample.length} {detail.sample.length === 1 ? 'row' : 'rows'}
        </div>
        {!hasRows ? (
          <div className="text-[10.5px]" style={{ color: C.text3 }}>
            No sample rows.
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ border: `1px solid ${C.border}`, borderRadius: 6 }}>
            <table className="border-collapse text-[10px]" style={{ minWidth: '100%' }}>
              <thead>
                <tr style={{ background: C.bgWarm }}>
                  {columns.map((k) => (
                    <th
                      key={k}
                      className="px-[10px] py-[6px] text-left font-bold font-mono whitespace-nowrap"
                      style={{ color: C.text3, borderBottom: `1px solid ${C.border}` }}
                    >
                      {k}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {detail.sample.map((row, i) => (
                  <tr key={i}>
                    {columns.map((k) => (
                      <td
                        key={k}
                        className="px-[10px] py-[5px] font-mono whitespace-nowrap"
                        style={{ color: C.text2, borderBottom: `1px solid ${C.border}` }}
                      >
                        {renderCellValue(row[k])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Fields */}
      <div className="px-6 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${C.border}` }}>
        <div className={cls(LABEL_SM, 'mb-[10px]')} style={{ color: C.text3 }}>
          Fields — {detail.fields.length}
        </div>
        {detail.fields.length === 0 ? (
          <div className="text-[10.5px]" style={{ color: C.text3 }}>
            No fields available.
          </div>
        ) : (
          detail.fields.map((f) => (
            <div
              key={f.name}
              className="flex items-center gap-[10px] py-[6px]"
              style={{ borderBottom: `1px solid ${C.border}` }}
            >
              <div
                className="text-[10.5px] font-bold font-mono"
                style={{ color: C.text1, overflowWrap: 'anywhere', flex: 1 }}
              >
                {f.name}
              </div>
              <div
                className="text-[9px] font-semibold px-[5px] py-px font-mono flex-shrink-0"
                style={{ borderRadius: 3, background: 'rgba(39,68,131,.08)', color: C.blueDark }}
              >
                {f.type}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

// ── PIPELINE VIEW ─────────────────────────────────────────────────────────────
const LAYER_NAMES: Record<string, string> = {
  raw: 'Raw',
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  platinum: 'Platinum',
};
const PL_COLS = ['raw', 'bronze', 'silver', 'gold', 'platinum'];
const PL_COL_COLOR: Record<string, string> = {
  raw: '#B91C1C',
  bronze: '#B45309',
  silver: '#94A3B8',
  gold: '#CA8A04',
  platinum: '#6D28D9',
};
const PL_HEADER_PAD = 44; // space reserved under the column header row so tables don't touch it
const PL_W = 1100;
const PL_H = 640; // minimum height; grows with the densest column
const NODE_H = 42;

function PipelineView({
  pipeline,
  focusId,
  onViewTableDetail,
  onBack,
}: {
  pipeline: Pipeline;
  focusId?: string | null;
  onViewTableDetail: (tableId: string) => void;
  onBack: () => void;
}) {
  // PipelineView mounts fresh each time the user enters the pipeline view, so initializing from
  // `focusId` highlights the table the user came from (via "See lineage").
  const [selectedId, setSelectedId] = useState<string | null>(focusId ?? null);
  const [transform, setTransform] = useState({ k: 1, x: 0, y: 0 });
  const dragRef = useRef<{ sx: number; sy: number; tx: number; ty: number } | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { nodes, edges } = pipeline;
  const colW = PL_W / PL_COLS.length;

  // Height grows with the densest column so dense layers (e.g. gold) don't overlap.
  const H = useMemo(() => {
    const counts: Record<number, number> = {};
    nodes.forEach((n) => {
      counts[n.col] = (counts[n.col] || 0) + 1;
    });
    const maxPerCol = Math.max(1, ...Object.values(counts));
    return Math.max(PL_H, PL_HEADER_PAD + maxPerCol * 56);
  }, [nodes]);

  const nodePos = useMemo(() => {
    const byCol: Record<number, PlNode[]> = {};
    nodes.forEach((n) => {
      (byCol[n.col] ??= []).push(n);
    });
    const pos: Record<string, { x: number; y: number }> = {};
    Object.entries(byCol).forEach(([col, ns]) => {
      const cx = parseFloat(col) * colW + colW / 2;
      // Lay tables out below the header band so the first row never touches the layer labels.
      const spacing = (H - PL_HEADER_PAD) / (ns.length + 1);
      ns.forEach((n, i) => {
        pos[n.id] = { x: cx, y: PL_HEADER_PAD + spacing * (i + 1) };
      });
    });
    return pos;
  }, [nodes, colW, H]);

  // On entry via "See lineage", center + zoom the focused node (measured against the container so the
  // on-screen size is readable regardless of how tall the graph is). Runs once on mount (before paint).
  useLayoutEffect(() => {
    if (!focusId) return;
    const pos = nodePos[focusId];
    const el = containerRef.current;
    if (!pos || !el) return;
    const rect = el.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    // viewBox→screen fit scale (preserveAspectRatio="meet"); pick k so the node renders ~comfortably.
    const meet = Math.min(rect.width / PL_W, rect.height / H);
    const k = meet > 0 ? Math.min(2.4, Math.max(0.8, 0.8 / meet)) : 1.4;
    // Center the node in the viewBox (which is centered in the container by `meet`).
    setTransform({ k, x: PL_W / 2 - k * pos.x, y: H / 2 - k * pos.y });
  }, [focusId, nodePos, H]);

  const lineage = selectedId ? pipeline.lineage[selectedId] : undefined;
  const upSet = new Set(lineage?.up ?? []);
  const dnSet = new Set(lineage?.dn ?? []);

  function edgeColor(e: PlEdge): { stroke: string; width: number; opacity: number } {
    if (!selectedId) return { stroke: '#CBD5E1', width: 1.5, opacity: 0.5 };
    // amber: upstream flow into selected node
    if ((upSet.has(e.s) && upSet.has(e.t)) || (e.t === selectedId && upSet.has(e.s))) {
      return { stroke: '#F59E0B', width: 2.5, opacity: 0.8 };
    }
    // blue: downstream from selected node
    if (e.s === selectedId && dnSet.has(e.t)) {
      return { stroke: '#274483', width: 2.5, opacity: 0.8 };
    }
    return { stroke: '#E2E8F0', width: 1, opacity: 0.2 };
  }

  function nodeFill(id: string): string {
    if (!selectedId) return 'white';
    if (id === selectedId) return 'rgba(39,68,131,.06)';
    if (upSet.has(id)) return 'rgba(245,158,11,.08)';
    if (dnSet.has(id)) return 'rgba(39,68,131,.08)';
    return 'white';
  }

  const det = selectedId ? pipeline.tableDetails[selectedId] : undefined;
  const selectedNode = selectedId ? nodes.find((n) => n.id === selectedId) : undefined;

  // Convert a screen (client) point into the SVG's viewBox coordinate space (before the <g> transform).
  const clientToSvg = (clientX: number, clientY: number) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const ctm = svg.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    const p = svg.createSVGPoint();
    p.x = clientX;
    p.y = clientY;
    const r = p.matrixTransform(ctm.inverse());
    return { x: r.x, y: r.y };
  };

  // Zoom toward the cursor: keep the point under the mouse fixed while scaling.
  const onWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const p = clientToSvg(e.clientX, e.clientY);
    setTransform((t) => {
      const k2 = Math.min(3, Math.max(0.3, t.k * (e.deltaY < 0 ? 1.1 : 1 / 1.1)));
      return { k: k2, x: p.x - (k2 / t.k) * (p.x - t.x), y: p.y - (k2 / t.k) * (p.y - t.y) };
    });
  };
  const onMouseDown = (e: React.MouseEvent) => {
    const p = clientToSvg(e.clientX, e.clientY);
    dragRef.current = { sx: p.x, sy: p.y, tx: transform.x, ty: transform.y };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragRef.current) return;
    const p = clientToSvg(e.clientX, e.clientY);
    const d = dragRef.current;
    setTransform((t) => ({ ...t, x: d.tx + (p.x - d.sx), y: d.ty + (p.y - d.sy) }));
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  return (
    <div
      className="flex flex-1 min-h-0 overflow-hidden bg-white"
      style={{ border: `1px solid ${C.border}`, borderRadius: 8 }}
    >
      {/* Graph */}
      <div ref={containerRef} className="flex-1 relative overflow-hidden" style={{ background: '#F8FAFC' }}>
        <button
          type="button"
          onClick={onBack}
          className="absolute top-3 left-3 z-10 text-[11px] font-semibold cursor-pointer"
          style={{
            padding: '5px 10px',
            borderRadius: 6,
            background: 'white',
            border: `1px solid ${C.border}`,
            color: C.blueDark,
          }}
        >
          ← Back to table
        </button>
        <svg
          ref={svgRef}
          viewBox={`0 0 ${PL_W} ${H}`}
          className="absolute inset-0 w-full h-full"
          style={{ cursor: dragRef.current ? 'grabbing' : 'grab' }}
          onWheel={onWheel}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onClick={() => setSelectedId(null)}
        >
          <defs>
            <marker id="tfm-arr" viewBox="0 -4 8 8" refX={8} refY={0} markerWidth={5} markerHeight={5} orient="auto">
              <path d="M0,-4L8,0L0,4" fill="#CBD5E1" />
            </marker>
            <filter id="tfm-shadow">
              <feDropShadow dx={0} dy={1} stdDeviation={2} floodOpacity={0.08} />
            </filter>
          </defs>
          <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
            {/* Column headers + dividers */}
            {PL_COLS.map((layerKey, col) => {
              const cx = col * colW + colW / 2;
              const c = PL_COL_COLOR[layerKey] ?? '#6B7280';
              return (
                <g key={layerKey}>
                  <rect x={col * colW + 8} y={4} width={colW - 16} height={28} rx={4} fill={c} fillOpacity={0.12} />
                  <text
                    x={cx}
                    y={22}
                    textAnchor="middle"
                    fontSize={11}
                    fontWeight={700}
                    fill={c}
                    fontFamily="Inter,sans-serif"
                  >
                    {LAYER_NAMES[layerKey]}
                  </text>
                  {col > 0 && <line x1={col * colW} y1={0} x2={col * colW} y2={H} stroke="#E2E8F0" strokeWidth={1} />}
                </g>
              );
            })}
            {/* Edges */}
            {edges.map((e, i) => {
              const sp = nodePos[e.s];
              const tp = nodePos[e.t];
              if (!sp || !tp) return null;
              const mx = (sp.x + tp.x) / 2;
              const ec = edgeColor(e);
              return (
                <path
                  key={i}
                  d={`M${sp.x},${sp.y} C${mx},${sp.y} ${mx},${tp.y} ${tp.x},${tp.y}`}
                  fill="none"
                  stroke={ec.stroke}
                  strokeWidth={ec.width}
                  strokeOpacity={ec.opacity}
                  markerEnd="url(#tfm-arr)"
                />
              );
            })}
            {/* Nodes */}
            {nodes.map((n) => {
              const p = nodePos[n.id];
              if (!p) return null;
              const c = n.color;
              const w = colW - 32;
              const lines = n.label.split('\n');
              // Truncate long names to fit the node box (full name on hover via <title>).
              const maxChars = Math.max(6, Math.floor((w - 18) / 5.6));
              const truncate = (s: string) => (s.length > maxChars ? s.slice(0, maxChars - 1) + '…' : s);
              const selected = n.id === selectedId;
              return (
                <g
                  key={n.id}
                  transform={`translate(${p.x - w / 2},${p.y - NODE_H / 2})`}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(n.id);
                  }}
                >
                  <title>{n.label}</title>
                  <rect
                    width={w}
                    height={NODE_H}
                    rx={6}
                    fill={nodeFill(n.id)}
                    stroke={c}
                    strokeWidth={1.5}
                    strokeOpacity={selectedId ? (selected || upSet.has(n.id) || dnSet.has(n.id) ? 1 : 0.3) : 0.4}
                    filter="url(#tfm-shadow)"
                  />
                  <rect width={4} height={NODE_H} rx={2} fill={c} fillOpacity={0.8} />
                  <text
                    x={12}
                    y={lines.length > 1 ? 14 : 16}
                    fontSize={10}
                    fontWeight={700}
                    fill="#1E293B"
                    fontFamily="Inter,sans-serif"
                  >
                    {truncate(lines[0])}
                  </text>
                  {lines[1] && (
                    <text x={12} y={26} fontSize={9} fontWeight={600} fill="#64748B" fontFamily="Inter,sans-serif">
                      {truncate(lines[1])}
                    </text>
                  )}
                  <text x={12} y={lines.length > 1 ? 37 : 30} fontSize={9} fill="#94A3B8" fontFamily="Inter,sans-serif">
                    {n.sub}
                  </text>
                </g>
              );
            })}
          </g>
        </svg>
        {/* Hint */}
        <div
          className="absolute top-3 right-3 px-[10px] py-[5px] text-[10px]"
          style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 6, color: C.text3 }}
        >
          Click any table to trace its lineage · Scroll to zoom · Drag to pan
        </div>
      </div>

      {/* Right detail panel */}
      <div
        className="w-[300px] flex-shrink-0 flex flex-col overflow-hidden bg-white"
        style={{ borderLeft: `1px solid ${C.border}` }}
      >
        {!selectedId ? (
          <div
            className="flex-1 flex flex-col items-center justify-center gap-2 p-6 text-center"
            style={{ color: C.text3 }}
          >
            <div className="text-[28px]" style={{ opacity: 0.15 }}>
              ⟳
            </div>
            <div className="text-[12px] font-semibold" style={{ color: C.text2 }}>
              Click any table
            </div>
            <div className="text-[11px] leading-[1.6] max-w-[220px]">
              Select a table to highlight its lineage — upstream sources light up in amber, downstream consumers in
              blue.
            </div>
          </div>
        ) : det ? (
          <div className="flex-1 overflow-y-auto flex flex-col">
            <div className="px-4 py-[14px] flex-shrink-0" style={{ borderBottom: `1px solid ${C.border}` }}>
              <div
                className="text-[9px] font-bold uppercase tracking-[0.07em] mb-[3px]"
                style={{ color: selectedNode?.color ?? C.blueDark }}
              >
                {det.layer} Layer
              </div>
              <div
                className="text-[13px] font-bold font-mono mb-1"
                style={{ color: C.text1, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}
              >
                {det.name}
              </div>
              <div className="text-[11px] leading-[1.5] mb-2" style={{ color: C.text2 }}>
                {det.desc}
              </div>
              <div className="text-[9px] font-bold uppercase tracking-[0.07em]" style={{ color: C.text3 }}>
                Records
              </div>
              <div className="text-[14px] font-bold" style={{ color: C.text1 }}>
                {det.records}
              </div>
            </div>
            <div className="px-4 py-[14px]" style={{ borderBottom: `1px solid ${C.border}` }}>
              <div className="text-[9px] font-bold uppercase tracking-[0.07em] mb-[6px]" style={{ color: C.blueDark }}>
                How Keystone Uses This
              </div>
              <div
                className="text-[11px] leading-[1.6] px-[10px] py-2"
                style={{
                  background: 'rgba(39,68,131,.04)',
                  borderRadius: 6,
                  borderLeft: `2px solid ${C.blueDark}`,
                  color: C.text2,
                }}
              >
                {det.keystone}
              </div>
            </div>
            <div className="px-4 py-[14px]">
              <div className="text-[9px] font-bold uppercase tracking-[0.07em] mb-[6px]" style={{ color: C.text3 }}>
                Lineage Key
              </div>
              <div className="flex flex-col gap-[6px]">
                <div className="flex items-center gap-2">
                  <div style={{ width: 24, height: 3, background: '#F59E0B', borderRadius: 2 }} />
                  <span className="text-[11px]" style={{ color: C.text2 }}>
                    Upstream sources (amber)
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div style={{ width: 24, height: 3, background: '#274483', borderRadius: 2 }} />
                  <span className="text-[11px]" style={{ color: C.text2 }}>
                    Downstream consumers (blue)
                  </span>
                </div>
              </div>
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => selectedId && onViewTableDetail(selectedId)}
                  className="text-[11px] font-semibold cursor-pointer p-0"
                  style={{ color: C.blueDark, background: 'none', border: 'none' }}
                >
                  → View table detail
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 py-[14px]">
              <div
                className="text-[9px] font-bold uppercase tracking-[0.07em] mb-[3px]"
                style={{ color: selectedNode?.color ?? C.blueDark }}
              >
                {selectedNode ? `${selectedNode.layer.toUpperCase()} LAYER` : ''}
              </div>
              <div
                className="text-[13px] font-bold font-mono mb-[6px]"
                style={{ color: C.text1, overflowWrap: 'anywhere' }}
              >
                {selectedNode ? selectedNode.label : selectedId}
              </div>
              <div className="text-[11px]" style={{ color: C.text2 }}>
                {selectedNode?.sub}
              </div>
              {lineage && (
                <div className="mt-3 text-[10.5px] leading-[1.6]" style={{ color: C.text3 }}>
                  Upstream sources highlighted in amber.
                  <br />
                  Downstream consumers in blue.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
